import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { GroupSharingService } from '../group-sharing.service.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { SpotlightService } from '../../../core/services/spotlight.service';
import { BehaviorSubject, EMPTY, forkJoin, Observable, of } from 'rxjs';
import { FormControl, Validators } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { RsvpService } from '../../../core/services/rsvp/rsvp.service';
import { RSVPStatus } from '../../../core/models/rsvp.models';

@Component({
  selector: 'app-edit-group-popup',
  templateUrl: './edit-group-popup.component.html',
  styleUrls: ['./edit-group-popup.component.scss'],
})
export class EditGroupPopupComponent implements OnInit {
  displayedColumns = ['name', 'rsvpStatus', 'action'];

  rsvpTooltips: Record<RSVPStatus, string> = {
    [RSVPStatus.UNKNOWN]: 'The participant has not responded to the RSVP. They can still join the Group Sharing experience.',
    [RSVPStatus.ACCEPTED]: 'The participant has accepted the RSVP!',
    [RSVPStatus.REJECTED]: 'The participant has rejected the RSVP. The host may remove the participant if they wish.'
  }

  rsvpStatusDisplayNames: Record<RSVPStatus, string> = {
    ACCEPTED: "Accepted",
    REJECTED: "Declined",
    UNKNOWN: "Unknown"
  }

  loading = true;
  watchPartyDetails: any = {};
  cinejoyEvent: any = {};
  groupName = '';
  invalidGroupName = false;

  userInput = new FormControl('');
  invitationEmail = new FormControl('', Validators.email);
  invitedEmails: string[] = [];
  selectedUserIds: number[] = [];
  selectedUsers: any[] = [];
  tableData: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  filteredUsers = of([]);
  filterHasLength = true;
  invalidUserIds = false;
  email = false
  userEmail:any

  constructor(
    private readonly groupSharingService: GroupSharingService,
    private readonly spotlightService: SpotlightService,
    private readonly rsvpService: RsvpService,
    public dialogRef: MatDialogRef<EditGroupPopupComponent>,
    private _snackBar: MatSnackBar,
    private router: Router,
    private toast: HotToastService,
    private route: ActivatedRoute,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  ngOnInit(): void {
    this.filteredUsers = this.userInput.valueChanges.pipe(
      startWith(''),
      debounceTime(600),
      distinctUntilChanged(),
      switchMap((val) => {
        return this.filter(val || '');
      }),
    );

    this.initialize();
  }

  initialize() {
    this.loading = true;


    this.spotlightService.getUserDetails().toPromise().then((res: any) => {

      this.userEmail = res.email

    }).catch((err: any) => {

    });
    forkJoin([
      this.groupSharingService.getActiveWatchParty(this.data.eventId),
      this.spotlightService.getPremierdeatails(this.data.eventId),
    ]).subscribe({
      next: ([watchParties, eventDetails]) => {
        this.buildWatchPartyDetailsForUI(watchParties);
        this.cinejoyEvent = eventDetails;
      },
      error: (err) => {
        console.error('Failed to initialize component. Cause: ', err);
        this.closePopup();
      },
    });
  }

  private buildWatchPartyDetailsForUI(watchParties: any[]) {
    let item: Record<string, string> = {};

    if (localStorage.getItem('userData')) {
      item = JSON.parse(localStorage.getItem('userData'));
    }

    item['userId'] = item.userID ? item.userID : item.userId;

    this.watchPartyDetails = watchParties.find((v) => v.userId == item.userId);
    this.groupName = this.watchPartyDetails.description;

    if (!this.watchPartyDetails) {
      throw Error('Watch Party not found!');
    }

    this.rsvpService
      .listRsvpStatusForType(
        Number(item.userId),
        this.watchPartyDetails.customRoomId,
      )
      .subscribe({
        next: (rsvpResponse) => {
          this.watchPartyDetails.participants.forEach((v: any) => {
            this.selectedUserIds.push(v.userId);
            this.selectedUsers.push({
              id: v.userId,
              name: v.userName === 'Invited User .' ? `Invited User (${v.userEmail})` : v.userName,
              userEmail: v.userEmail,
              rsvpStatus:
                rsvpResponse.find((rsvp) => rsvp.participantId === v.userId)
                  ?.status || RSVPStatus.UNKNOWN,
            });
          });
          this.tableData.next(this.selectedUsers);
          this.loading = false;
        },
        error: (err) => {
          console.error('Failed to list rsvp status! Defaulting to UNKNOWN. Cause: ', err);

          this.watchPartyDetails.participants.forEach((v: any) => {
            this.selectedUserIds.push(v.userId);
            this.selectedUsers.push({
              id: v.userId,
              name: v.userName === 'Invited User .' ? v.userEmail : v.userName,
              userEmail: v.userEmail,
              rsvpStatus: RSVPStatus.UNKNOWN,
            });
          });
          this.tableData.next(this.selectedUsers);
          this.loading = false;
        },
      });
  }

  closePopup() {
    this.dialogRef.close();
  }

  checkValidValue() {
    this.invalidGroupName =
      !this.groupName?.length ||
      this.groupName.length < 3 ||
      this.groupName.length >= 30;
  }

  filter(val: string): Observable<any[]> {
    this.invitationEmail.setValue(this.userInput.value, {
      onlySelf: true,
      emitEvent: true,
    });
    if (val.length > 2) {
      return this.groupSharingService.getPublicUsersList(val).pipe(
        map((response) =>
          response.filter((option) => {
            let item: any = '';
            if (localStorage.getItem('userData')) {
              item = JSON.parse(localStorage.getItem('userData'));
            }
            return (
              option.userId != item.userId &&
              !this.selectedUsers.find((p) => p.id == option.userId)
            );
          }),
        ),
        map((response) => {
          this.filterHasLength = !!response.length;

          const check = this.invitationEmail.value
            
          const emailRegex: RegExp = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

          if (emailRegex.test(check)) {
            console.log("valid email address");
            this.email = true
          } else {
            console.log("Invalid email address");
            this.email = false
          }
          if(check === this.userEmail){
            this.email = false
          }
          if (this.invitationEmail.value && !this.filterHasLength && this.email) {
            this.toast.info(
              `User not found on the Creatics Platform. Please click on the 'Invite User' button to invite them.`,
              {
                theme: 'snackbar',
                position: 'top-right',
                duration: 3000
              }
            );
          }

          if (this.invitationEmail.value && !this.filterHasLength && !this.email) {


            if(check === this.userEmail){
              this.email = false

              this.toast.info(
                `You can't invite yourself!`,
                {
                  theme: 'snackbar',
                  position: 'top-right',
                  duration: 3000
                }
              );

            }
            else{
              this.toast.info(
                `Please enter valid email address`,
                {
                  theme: 'snackbar',
                  position: 'top-right',
                  duration: 2000
                }
              );
            }
          }

          return response;
        }),
      );
    }
    return EMPTY;
  }

  selectUser($event: MatAutocompleteSelectedEvent) {
    this.selectedUsers.splice(0, 0, {
      id: $event.option.value.userId,
      name: `${$event.option.value.firstName} ${$event.option.value.lastName}`,
      userEmail: $event.option.value.userEmail,
      rsvpStatus: RSVPStatus.UNKNOWN,
      newParticipant: true,
      invited: false,
    });
    this.selectedUserIds.push($event.option.value.userId);
    this.userInput.reset('');
    this.tableData.next(this.selectedUsers);
  }

  inviteUser($event: any) {
    $event.stopImmediatePropagation();

    if (!this.invitationEmail.valid) {
      return;
    }

    if (this.invitedEmails.includes(this.invitationEmail.value)) {
      this._snackBar.open('User already added for invitation', '');
      return;
    }
    this.invitedEmails.push(this.invitationEmail.value);
    this.selectedUsers.splice(0, 0, {
      id: '-',
      name: `Invited User (${this.invitationEmail.value})`,
      userEmail: this.invitationEmail.value,
      rsvpStatus: RSVPStatus.UNKNOWN,
      newParticipant: true,
      invited: true
    });
    this.tableData.next(this.selectedUsers);
    this.invitationEmail.setValue('', { emitEvent: true });
    this.userInput.reset('', { emitEvent: true });
  }

  removeParticipant(user: any): void {
    const index = this.selectedUsers.indexOf(user);
    const idIndex = this.selectedUserIds.indexOf(user.id);
    if (index >= 0) {
      this.selectedUsers.splice(index, 1);
    }
    if (idIndex >= 0) {
      this.selectedUserIds.splice(idIndex, 1);
    }
    if (user.invited) {
      this.removeInvitedUser(user);
    }
    this.tableData.next(this.selectedUsers);
  }

  removeInvitedUser(user: any): void {
    let index = this.invitedEmails.findIndex(emailEntry => emailEntry === user.userEmail);
    if (index >= 0) {
      this.invitedEmails.splice(index, 1);
    }
  }

  cancelEdit() {
    this.dialogRef.close();
  }

  updateWatchParty() {
    console.log('updating watch party');
  }

  updateEvent() {
    this.loading = true;
    if (this.selectedUserIds.length <= 0) {
      this.invalidUserIds = true;
      this.invalidUserIds = this.invitedEmails.length <= 0;
    } else {
      this.invalidUserIds = false;
    }

    this.invalidGroupName = this.groupName == null || this.groupName.length < 3;

    if (this.invalidGroupName || this.invalidUserIds) {
      this.loading = false;
      return;
    }

    const obj = {
      eventId: this.watchPartyDetails.eventId,
      description: this.groupName,
      expiryTime: '2023-10-20T11:34:51.513Z',
      participantIds: this.selectedUserIds,
    };
    this.groupSharingService
      .updateEvent(obj, this.watchPartyDetails.customRoomId)
      .subscribe(
        (res) => {
          res.invitedEmails = this.invitedEmails;
          this.dialogRef.close(res);
          this.toast.info(`Event has been successfully updated.`, {
            theme: 'snackbar',
            position: 'top-right',
            duration: 3000,
          });
        },
        (err) => {
          this.toast.info(err, {
            theme: 'snackbar',
            position: 'top-right',
            duration: 3000,
          });
        },
      );
  }

  deleteGroup() {
    this.loading = true;
    if (window.confirm('Are sure you want to delete this group ?')) {
      this.groupSharingService
        .deleteEvent(this.watchPartyDetails.customRoomId, this.watchPartyDetails.eventId)
        .subscribe(
          (res) => {
            this.toast.info(`Group has been successfully deleted.`, {
              theme: 'snackbar',
              position: 'top-right',
              duration: 3000,
            });
            this.loading = false;
            this.closePopup();
          },
          (err) => {
            this.toast.info(err.error, {
              theme: 'snackbar',
              position: 'top-right',
              duration: 3000,
            });
            this.loading = false;
          },
        );
    } else {
      this.loading = false;
    }
  }
}
